import { formatAuthorName } from 'utils/formatters';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import BaseTableOptionsDropdown from 'components/atoms/base-table-option-dropdown';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { type FC } from 'react';
import { DOCUMENTATION_TIME_PLACEHOLDER } from 'infrastructure/consts/tables';
import { convertSecondsToTime } from 'utils/timeHelper';
import { Flex, Tag } from 'antd';

import s from './styles.module.scss';
import { MedicationInfo } from './info';

import type {
  IMedicationForUpdate,
  IMedication,
} from 'infrastructure/interfaces';

interface MedicationItemProps {
  item: IMedication;
  disableActions: boolean;
  onDelete: (guid: string) => void;
  onEdit: (item: IMedicationForUpdate) => void;
  loading: boolean;
  modifyItemGuid?: string;
}

const MedicationItem: FC<MedicationItemProps> = ({
  item,
  disableActions,
  loading,
  modifyItemGuid,
  onDelete,
  onEdit,
}) => {
  const { t } = useTranslation();

  const { firstName, lastName, title } = item.updatedBy ?? item.author;
  const authorName = formatAuthorName({
    firstName,
    lastName,
    professionType: '',
    title,
  });
  const lastEditDate = DateFormatter({
    date: item.updatedAt,
    format: DateFormats['MM/dd/yyyy'],
  });

  const dropdownInfo = (
    <p className={s['dropdown-info']}>
      <span>
        {t('labels.lastEditedBy')}: {authorName},
      </span>
      <span>{lastEditDate}</span>
    </p>
  );

  const handleEdit = () => {
    const dosageMatch = item.dosage?.match(/^(\d+(?:\.\d+)?)\s*(.*)$/);
    const dosageValue = dosageMatch?.[1] ?? item.dosage ?? '';
    const unitValue = dosageMatch?.[2] ?? '';

    onEdit({
      dosage: dosageValue,
      unit: unitValue,
      duration: item.duration,
      endDate: item.endDate ? dayjs(item.endDate).toDate() : undefined,
      startDate: item.startDate ? dayjs(item.startDate).toDate() : undefined,
      frequency: item.frequency ?? '',
      name: item.name,
      isActive: item.isActive,
      note: item.note ?? '',
      route: item.route ?? '',
      guid: item.guid,
    });
  };

  const actionTime = !item?.documentationTime
    ? DOCUMENTATION_TIME_PLACEHOLDER
    : convertSecondsToTime(item?.documentationTime);

  return (
    <Flex vertical className={s.wrapper} data-cy="medication-item">
      <div className={s.header}>
        <div className={s['header-left']} data-cy="medication-name">
          <h5 className={s.title}>{item.name} </h5>
          <Tag
            color={item.isActive ? 'success' : 'default'}
            data-cy="medication-status"
            className={s.tag}
          >
            {item.isActive ? t('labels.active') : t('labels.inactive')}
          </Tag>
        </div>
        <div className={s['header-right']}>
          <p className={s['documentation-time']}>
            {t('labels.documentationTime')}: {actionTime}
          </p>
          <BaseTableOptionsDropdown
            loading={loading && item.guid === modifyItemGuid}
            onDelete={() => {
              onDelete(item.guid);
            }}
            onEdit={handleEdit}
            disabled={disableActions}
            info={dropdownInfo}
            bordered
            triggerBtnClassName={s['dropdown-btn']}
          />
        </div>
      </div>
      <div className={s.body} data-cy="medication-description">
        <MedicationInfo item={item} />
      </div>
    </Flex>
  );
};
export default MedicationItem;
